import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { ProgressRecord, ProgressPhoto } from '@/lib/types';

export function useProgress(studentId: string) {
  const queryClient = useQueryClient();

  const recordsQuery = useQuery({
    queryKey: ['progress-records', studentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('progress_records')
        .select('*')
        .eq('student_id', studentId)
        .order('recorded_at', { ascending: true });
      if (error) throw error;
      return data as ProgressRecord[];
    },
    enabled: !!studentId,
  });

  const photosQuery = useQuery({
    queryKey: ['progress-photos', studentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('progress_photos')
        .select('*')
        .eq('student_id', studentId)
        .order('taken_at', { ascending: false });
      if (error) throw error;
      return data as ProgressPhoto[];
    },
    enabled: !!studentId,
  });

  const { mutateAsync: addRecord } = useMutation({
    mutationFn: async (input: { weight_kg: number; body_fat_pct?: number; measurements?: Record<string, number> }) => {
      const { data, error } = await supabase
        .from('progress_records')
        .insert({ student_id: studentId, ...input })
        .select()
        .single();
      if (error) throw error;
      return data as ProgressRecord;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['progress-records', studentId] }),
  });

  const { mutateAsync: addRecordForStudent } = useMutation({
    mutationFn: async (input: {
      studentId: string;
      weight_kg: number;
      body_fat_pct?: number;
      recorded_at: string;
      measurements?: Record<string, number | string>;
    }) => {
      const { error } = await supabase
        .from('progress_records')
        .insert({
          student_id: input.studentId,
          weight_kg: input.weight_kg,
          body_fat_pct: input.body_fat_pct ?? null,
          recorded_at: input.recorded_at,
          measurements: input.measurements ?? {},
        });
      if (error) throw error;
    },
    onSuccess: (_, variables) =>
      queryClient.invalidateQueries({ queryKey: ['progress-records', variables.studentId] }),
  });

  const { mutateAsync: uploadPhoto } = useMutation({
    mutationFn: async ({ uri, ownerId }: { uri: string; ownerId: string }) => {
      const fileName = `${ownerId}/${Date.now()}.jpg`;
      const response = await fetch(uri);
      const blob = await response.blob();
      const { error } = await supabase.storage.from('progress-photos').upload(fileName, blob, { contentType: 'image/jpeg' });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('progress-photos').getPublicUrl(fileName);
      const { data: photo, error: photoError } = await supabase
        .from('progress_photos')
        .insert({ student_id: ownerId, photo_url: publicUrl })
        .select()
        .single();
      if (photoError) throw photoError;
      return photo as ProgressPhoto;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['progress-photos', studentId] }),
  });

  return {
    records: recordsQuery.data ?? [],
    photos: photosQuery.data ?? [],
    loading: recordsQuery.isLoading || photosQuery.isLoading,
    addRecord,
    addRecordForStudent,
    uploadPhoto: (uri: string, ownerId: string) => uploadPhoto({ uri, ownerId }),
    refetch: recordsQuery.refetch,
  };
}
